/**
 * Product Schemas
 *
 * Zod schemas for validating product-related API responses.
 */

import { z } from 'zod';

// Product schema
export const ProductSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string(),
  category: z.string().nullable().optional(),
  subcategory: z.string().nullable().optional(),
  buyer_id: z.number().optional(),
  buyer_name: z.string().optional(),
  supplier_id: z.number().optional(),
  supplier_name: z.string().optional(),
  latest_purchase_price: z.number().nullable().optional(),
  latest_purchase_unit: z.string().nullable().optional(),
  latest_purchase_unit_value: z.string().nullable().optional(),
});

export type Product = z.infer<typeof ProductSchema>;

// Products response schema
export const ProductsResponseSchema = z.object({
  products: z.array(ProductSchema),
});

export type ProductsResponse = z.infer<typeof ProductsResponseSchema>;

// Product item (simplified)
export const ProductItemSchema = z.object({
  id: z.number(),
  name: z.string(),
  code: z.string(),
  category: z.string().nullable().optional(),
  subcategory: z.string().nullable().optional(),
});

export type ProductItem = z.infer<typeof ProductItemSchema>;

// Organisation products response
export const OrganisationProductsResponseSchema = z.object({
  products: z.array(ProductItemSchema),
});

export type OrganisationProductsResponse = z.infer<typeof OrganisationProductsResponseSchema>;

// CSV preview response
export const CsvPreviewResponseSchema = z.object({
  columns: z.array(z.string()),
  first_row_preview: z.record(z.string(), z.string()),
  available_fields: z.array(z.object({
    value: z.string(),
    label: z.string(),
  })),
});

export type CsvPreviewResponse = z.infer<typeof CsvPreviewResponseSchema>;

// CSV upload response
export const CsvUploadResponseSchema = z.object({
  created: z.number(),
  updated: z.number(),
  skipped: z.number(),
  errors: z.array(z.string()).optional(),
});

export type CsvUploadResponse = z.infer<typeof CsvUploadResponseSchema>;

// Add product request
export const AddProductRequestSchema = z.object({
  user_id: z.string(),
  organisation_id: z.string(),
  product_name: z.string().min(1, 'Product name is required'),
  product_code: z.string().optional(),
  category: z.string().optional(),
  subcategory: z.string().optional(),
  supplier_name: z.string().optional(),
  buyer_name: z.string().optional(),
  view_mode: z.enum(['sold', 'purchased']),
});

export type AddProductRequest = z.infer<typeof AddProductRequestSchema>;

// Add product response
export const AddProductResponseSchema = z.object({
  created: z.boolean(),
  product_id: z.number().optional(),
  message: z.string().optional(),
});

export type AddProductResponse = z.infer<typeof AddProductResponseSchema>;
